import { ProviderService } from "./../provider.service";
import { inject, Injectable } from "@angular/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { Capacitor } from "@capacitor/core";
import { Platform } from "@ionic/angular";
import { LocalStorageService } from "../localStorage.service";
import { HttpClient } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { AppService } from "../app.service";
import { PusherBackground } from "../pusher-background.service";
import { PushNavigationService } from "../push-navigation.service";

@Injectable({
  providedIn: "root",
})
export class PushNotificationService {
  private readonly pushNavigationService = inject(PushNavigationService);

  isIosDevice!: boolean;
  isAndroidDevice!: boolean;
  isNotWeb!: boolean;
  private listenersRegistered = false;
  private forceRefreshNextIosFcmToken = false;
  url = `${environment.apiUrl}`;
  authUrl = `${environment.authUrl}`;
  constructor(
    public platform: Platform,
    public localStorageService: LocalStorageService,
    private http: HttpClient,
    private providerService: ProviderService,
    private appService: AppService
  ) {}

  checkPlatform(): boolean {
    const platformId = Capacitor.getPlatform();

    if (platformId === "web") {
      this.isIosDevice = false;
      this.isAndroidDevice = false;
      this.isNotWeb = false;
      return false;
    }

    this.isIosDevice = platformId === "ios" || this.platform.is("ios");
    this.isAndroidDevice = platformId === "android" || this.platform.is("android");
    this.isNotWeb = this.isIosDevice || this.isAndroidDevice;
    return this.isNotWeb;
  }

  initFcmPusher(options: { forceRefresh?: boolean } = {}) {
    if (options.forceRefresh) {
      this.forceRefreshNextIosFcmToken = true;
    }
    if (this.checkPlatform()) {
      void this.registerPusher();
    }
  }

  private async registerPusher() {
    try {
      await this.addListeners();

      let permStatus = await PushNotifications.checkPermissions();

      if (permStatus.receive === "prompt") {
        permStatus = await PushNotifications.requestPermissions();
      }

      if (permStatus.receive !== "granted") {
        throw new Error("User denied permissions!");
      }

      await PushNotifications.register();
    } catch {
      // Registration failed or permissions denied.
    }
  }

  addListeners = async () => {
    if (this.listenersRegistered) {
      return;
    }
    this.listenersRegistered = true;

    await PushNotifications.addListener("registration", async (token) => {
      // Sur iOS, Capacitor PushNotifications retourne l'APNS token brut
      // (hex 64 chars). Pour que le backend Firebase Admin SDK puisse livrer
      // le push, on a besoin du FCM token (résolu par Firebase Messaging à
      // partir de l'APNS token côté natif iOS — cf AppDelegate.messaging
      // didReceiveRegistrationToken). On le récupère via le plugin natif.
      // Sur Android, Capacitor retourne déjà directement le FCM token → on
      // l'utilise tel quel.
      const capacitorToken = token.value;

      let push_token = capacitorToken;

      if (Capacitor.getPlatform() === "ios") {
        try {
          const forceRefresh = this.forceRefreshNextIosFcmToken;
          this.forceRefreshNextIosFcmToken = false;
          const fcm = forceRefresh
            ? await PusherBackground.refreshFcmToken()
            : await PusherBackground.getFcmToken();
          if (fcm?.token) {
            push_token = fcm.token;
          }
        } catch {
          // Fallback to Capacitor token when native FCM lookup fails.
        }
      }

      const saved_fcm_token = this.localStorageService.getFcmToken();
      const tokenUnchanged = !!saved_fcm_token && push_token === saved_fcm_token;

      if (push_token) {
        this.saveToken(push_token).subscribe();
      }

      if (!tokenUnchanged && push_token) {
        this.localStorageService.setFcmToken(push_token);
      }
    });

    await PushNotifications.addListener("registrationError", () => {
      // Token registration failed.
    });

    // Listeners Capacitor PushNotifications : sur iOS, ils continuent à
    // fonctionner normalement. Sur Android, MyFirebaseMessagingService
    // intercepte le RemoteMessage en priorité et `pushNotificationReceived`
    // n'est PLUS appelé — les listeners ci-dessous s'occupent de la prise
    // en charge croisée via le plugin PusherBackground (cf addAndroidFcmListeners).
    await PushNotifications.addListener(
      "pushNotificationReceived",
      (notification) => {
        const data = this.extractNotificationData(notification);
        this.handleForegroundPushReceived(data);
      }
    );

    await PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (notification) => {
        const payload = notification.notification.data as Record<string, any>;
        // Dedup with native Android pushClicked is handled in PushNavigationService.enqueueFromPushPayload.
        this.pushNavigationService.enqueueFromPushPayload(payload);
      }
    );

    await this.addNativePushClickListeners();
  };

  /**
   * Sur Android, écoute les events `pushReceived` (push reçu en foreground)
   * et `pushClicked` (user clique la bannière) émis par
   * `MyFirebaseMessagingService` via `PusherBackgroundPlugin.notifyListeners`.
   *
   * Sans ce câblage, le routing au clic d'une bannière FCM serait perdu sur
   * Android (régression GPT-5.5 review Phase 2 #2).
   *
   * Pour le cas COLD START (app lancée par un clic FCM avant que le listener
   * soit enregistré), on consomme aussi le `getPendingPushClick()` natif
   * stocké par MainActivity (régression GPT-5.5 review #3).
   */
  private async addNativePushClickListeners() {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      if (Capacitor.getPlatform() === "android") {
        await PusherBackground.addListener("pushReceived", (data) => {
          const type = this.resolveNotificationType(data);
          if (type === "POP_PRE_ASSIGN_COMMAND") {
            this.appService.notifyDemandRetractionFeedback(data);
          }
          if (this.isNewPreAssignNotification(type)) {
            this.storeIncomingPreAssignedDemand(data);
          }
        });

        await PusherBackground.addListener("pushClicked", (data) => {
          this.pushNavigationService.enqueueFromPushPayload(data);
        });
      }

      await this.pushNavigationService.consumeNativePendingPushClick();
    } catch {
      // Native push click listeners unavailable.
    }
  }

  private resolveNotificationType(data: Record<string, any> | null | undefined): string | undefined {
    return data?.["type_event"] ?? data?.["event_type"] ?? data?.["type_notification"];
  }

  private extractNotificationData(notification: any): Record<string, any> {
    return notification?.data ?? notification?.notification?.data ?? notification ?? {};
  }

  private isNewPreAssignNotification(type: string | undefined): boolean {
    return type === "NEW_PRE_ASSIGN_DEMANDE" || type === "NEW_DEMANDE";
  }

  private resolveDemandReference(data: Record<string, any> | null | undefined): any {
    return (
      data?.["demande"] ??
      data?.["demande_id"] ??
      data?.["demand_id"] ??
      data?.["id_demande"] ??
      data?.["id"]
    );
  }

  private buildDemandPayload(data: Record<string, any>): Record<string, any> {
    const demandRef = this.resolveDemandReference(data);
    const payload: Record<string, any> = { ...data };
    if (payload["demande"] == null && demandRef != null) {
      payload["demande"] = demandRef;
    }
    if (
      payload["demande_id"] == null &&
      (typeof demandRef === "number" || typeof demandRef === "string")
    ) {
      payload["demande_id"] = demandRef;
    }
    return payload;
  }

  private storeIncomingPreAssignedDemand(data: Record<string, any>) {
    const role = (this.localStorageService.getUserRole() || "").toLowerCase();
    if (role !== "prestataire") {
      return;
    }
    this.providerService.storeAndDemandData(this.buildDemandPayload(data)).catch(() => undefined);
  }

  private handleForegroundPushReceived(data: Record<string, any>) {
    const type = this.resolveNotificationType(data);
    if (type === "POP_PRE_ASSIGN_COMMAND") {
      this.appService.notifyDemandRetractionFeedback(data);
      return;
    }
    if (this.isNewPreAssignNotification(type)) {
      this.storeIncomingPreAssignedDemand(data);
    }
  }

  getDeliveredNotifications = async () => {
    await PushNotifications.getDeliveredNotifications();
  };

  saveToken(token: string) {
    const mobile = this.checkPlatform();
    return this.http.post(`${this.authUrl}/frontoffice/save-token`, {
      token: token,
      platform: mobile ? "mobile" : "web",
    });
  }
}
